import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Calendar, Bot, Send } from "lucide-react";
import { adminApi } from "../../services/adminApi";

export default function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setIsLoading(true);
        const response = await adminApi.getUser(id);
        setUser(response.user);
        // Agents are included in the user object from backend
        setAgents(response.user?.agents || []);
      } catch (err) {
        console.error("Failed to fetch user:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUser();
  }, [id]);

  const handleEmailUser = () => {
    if (user?.email) {
      navigate(`/settings?tab=compose&to=${encodeURIComponent(user.email)}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <div className="p-6 lg:p-8 max-w-4xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-bg-secondary rounded w-1/4"></div>
          <div className="card p-6">
            <div className="h-20 bg-bg-secondary rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-6 lg:p-8 max-w-4xl mx-auto text-center">
        <p className="text-text-muted">User not found</p>
        <button onClick={() => navigate("/users")} className="btn-primary mt-4">
          Back to Users
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto">
      {/* Back Button */}
      <button
        onClick={() => navigate("/users")}
        className="flex items-center gap-2 text-text-secondary hover:text-text-primary mb-6"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to Users
      </button>

      {/* User Header */}
      <div className="card p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            {user.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-16 h-16 rounded-full"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-2xl font-medium text-primary">
                  {user.name?.charAt(0) || "U"}
                </span>
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-text-primary">
                {user.name || "Unknown User"}
              </h1>
              <div className="flex items-center gap-2 text-text-secondary mt-1">
                <Mail className="w-4 h-4" />
                {user.email}
              </div>
              <div className="flex items-center gap-4 mt-2">
                <span
                  className={`badge ${
                    user.isActive !== false ? "badge-success" : "badge-danger"
                  }`}
                >
                  {user.isActive !== false ? "Active" : "Inactive"}
                </span>
                <span className="flex items-center gap-1 text-sm text-text-muted">
                  <Calendar className="w-4 h-4" />
                  Joined {formatDate(user.createdAt)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleEmailUser} className="btn-primary">
              <Send className="w-4 h-4" />
              Email User
            </button>
          </div>
        </div>
      </div>

      {/* User Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-text-primary">
            {agents.length}
          </p>
          <p className="text-sm text-text-muted">Agents Created</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-text-primary">
            {agents.filter((a) => a.status === "active").length}
          </p>
          <p className="text-sm text-text-muted">Active Agents</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-text-primary">
            {agents.reduce((sum, a) => sum + (a.conversationsCount || 0), 0)}
          </p>
          <p className="text-sm text-text-muted">Total Conversations</p>
        </div>
      </div>

      {/* User's Agents */}
      <div className="card">
        <div className="p-4 border-b border-border">
          <h2 className="font-semibold text-text-primary">User's Agents</h2>
        </div>
        {agents.length === 0 ? (
          <div className="p-8 text-center text-text-muted">
            This user hasn't created any agents yet
          </div>
        ) : (
          <div className="divide-y divide-border">
            {agents.map((agent) => (
              <Link
                key={agent.id}
                to={`/agents/${agent.id}`}
                className="p-4 flex items-center gap-4 hover:bg-bg-secondary/50 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-text-primary">{agent.name}</p>
                  <p className="text-sm text-text-muted">
                    {agent.businessType || "AI Agent"}
                  </p>
                </div>
                <span
                  className={`badge ${
                    agent.status === "active"
                      ? "badge-success"
                      : "badge-warning"
                  }`}
                >
                  {agent.status}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
